/**
 * RebuildDayModal (ROUTE-05, first slice) — when a tech is OUT (sick, vacation),
 * his calls need new homes. Instead of the dispatcher hunting per call, Pegasus
 * proposes the best target for EVERY orphaned call at once:
 *   - skill-safe (target has every skill the call's notes imply),
 *   - honors requested techs ("Brent Only" goes to Brent if he's in),
 *   - never proposes an excluded tech ("do not send Camron"),
 *   - ranked by added drive miles (best insertion into the target's day),
 * with a per-row tech override, a per-row Review (the same MovePreview compare),
 * and one "Move N calls" commit + Undo all. Nothing writes until you commit —
 * the modal IS the before/after preview (review-before-commit discipline).
 *
 * Competitive frame: Salesforce Field Service auto-reshuffles on a "Resource
 * Absence" (we adopt the concept, human-in-the-loop); ServiceTitan parks jobs
 * in an unassigned tray to re-drag (the manual hunt we're replacing — that's
 * also ESC's model); Jobber/FieldEdge are fully manual.
 */
import { useMemo, useState } from "react";
import { ArrowRight, UserX, Undo2, CheckCircle2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import {
  formatTime,
  resolveWindowAndPriority,
  displayCustomerName,
} from "@/lib/format";
import {
  isPseudoDispatch,
  timeBand,
  windowColorClasses,
  useSetAssignment,
  useSaveRoutePlan,
  useTechSkills,
  useLocationNotes,
  type ActiveTech,
} from "./api";
import {
  deriveRequiredSkills,
  deriveRequestedTech,
  deriveRequestedTechs,
  deriveAttrLock,
  deriveExcludedTechs,
  skillName,
  type RecoTech,
  type RecoStop,
} from "@/lib/recommendations";
import {
  routeMiles,
  optimizeBanded,
  insertionCostMiles,
  driveMinutes,
  usableCoords,
} from "@/lib/route";
import { useTechAvailability, availabilityByTech } from "./availability";
import { MovePreview, type PendingMove } from "./MovePreview";
import { STATUS_DONE, type Dispatch } from "@/lib/types";
import { InfoTip } from "@/components/InfoTip";

interface Props {
  outEmpno: string;
  date: string; // YYYY-MM-DD (board date)
  rows: Dispatch[]; // the day's dispatches (assignments already applied)
  techs: ActiveTech[];
  onClose: () => void;
}

/** Per-call plan the dispatcher edits before committing. */
interface RowPlan {
  target?: string | null; // override target empno (undefined = use suggestion)
  include?: boolean; // override the include checkbox
  moved?: boolean; // already reassigned via the per-row Review
}

export function RebuildDayModal({ outEmpno, date, rows, techs, onClose }: Props) {
  const skillsData = useTechSkills().data;
  const locationNotes = useLocationNotes(
    rows.filter((r) => !isPseudoDispatch(r)).map((r) => r.custno),
  ).data;
  const availability = useTechAvailability(date).data ?? [];
  const setAssignment = useSetAssignment();
  const savePlan = useSaveRoutePlan();

  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);

  // Radix Modal supplies Esc/outside/✕ — but one Esc press must close ONE
  // layer: while the per-row Review (MovePreview) is open, this modal's close
  // requests are ignored (the Review handles its own).
  const guardedClose = () => {
    if (!pendingMove) onClose();
  };

  const nameFor = (empno: string) => {
    const t = techs.find((x) => x.empno === empno);
    const n =
      t?.empname ?? `${t?.firstname ?? ""} ${t?.lastname ?? ""}`.trim();
    return n && n.length ? n : empno;
  };
  const firstNameFor = (empno: string) =>
    techs.find((x) => x.empno === empno)?.firstname ?? nameFor(empno);

  // Who's out today (the rebuild tech + anyone else with an all-day event) —
  // never propose an out tech as a target.
  const outSet = useMemo(() => {
    const m = availabilityByTech(availability);
    const s = new Set(
      [...m.entries()].filter(([, v]) => v.out).map(([e]) => e),
    );
    s.add(outEmpno);
    return s;
  }, [availability, outEmpno]);

  const baseRows = useMemo(
    () => rows.filter((r) => !isPseudoDispatch(r)),
    [rows],
  );
  // The calls that need a new home: the out tech's not-yet-done work.
  const orphans = useMemo(
    () =>
      baseRows
        .filter(
          (r) =>
            r.tech_empno === outEmpno && !STATUS_DONE.has(r.status ?? ""),
        )
        .sort((a, b) => (a.tpromtime ?? "~").localeCompare(b.tpromtime ?? "~")),
    [baseRows, outEmpno],
  );

  // Roster + per-tech stop lists (same recipe as the board's recommendation
  // inputs) — EVERY active tech is a candidate, including ones with no calls
  // yet today (an idle qualified tech is often the best target).
  const recoTechs = useMemo<RecoTech[]>(() => {
    const roster = techs.map((t) => ({ empno: t.empno, first: t.firstname }));
    const mkStop = (r: Dispatch): RecoStop | null => {
      if (!usableCoords(r.loc_latitude, r.loc_longitude)) return null;
      const locNote = locationNotes?.[`${r.custno}|${r.locno}`] ?? "";
      const combined = `${r.notes ?? ""}\n${locNote}`;
      const excludedEmpnos = deriveExcludedTechs(combined, roster);
      let reqEmpno = deriveRequestedTech(combined, roster);
      if (reqEmpno && excludedEmpnos.includes(reqEmpno)) reqEmpno = null;
      return {
        dispatch: r.dispatch,
        // usableCoords() above guarantees these are numbers (TS can't narrow
        // across a two-arg predicate, hence the assertions).
        lat: r.loc_latitude!,
        lng: r.loc_longitude!,
        label: displayCustomerName(r.locname, r.customer_fullname),
        city: r.city,
        band: timeBand(r),
        requiredSkills: deriveRequiredSkills(r.notes),
        requestedEmpno: reqEmpno,
        requestedName: reqEmpno
          ? roster.find((x) => x.empno === reqEmpno)?.first ?? null
          : null,
        requestedEmpnos: deriveRequestedTechs(combined, roster).filter((e) => !excludedEmpnos.includes(e)),
        excludedEmpnos,
        attrLock: deriveAttrLock(combined),
      };
    };
    const byTech = new Map<string, RecoStop[]>(
      techs.map((t) => [t.empno, []]),
    );
    for (const r of baseRows) {
      const s = mkStop(r);
      if (!s) continue;
      const arr = byTech.get(r.tech_empno) ?? [];
      arr.push(s);
      byTech.set(r.tech_empno, arr);
    }
    return [...byTech.entries()].map(([empno, stops]) => ({
      empno,
      name: nameFor(empno),
      skills: new Set(skillsData?.[empno] ?? []),
      stops,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRows, techs, skillsData, locationNotes]);

  const techByEmpno = useMemo(
    () => new Map(recoTechs.map((t) => [t.empno, t] as const)),
    [recoTechs],
  );

  // Per-orphan analysis: signals + eligible targets + the suggestion.
  const analyses = useMemo(() => {
    return orphans.map((r) => {
      const outTech = techByEmpno.get(outEmpno);
      const stop = outTech?.stops.find((s) => s.dispatch === r.dispatch) ?? null;
      const hasCoords = Boolean(stop);
      const locNote = locationNotes?.[`${r.custno}|${r.locno}`] ?? "";
      const combined = `${r.notes ?? ""}\n${locNote}`;
      const roster = techs.map((t) => ({ empno: t.empno, first: t.firstname }));
      const required = stop?.requiredSkills ?? deriveRequiredSkills(r.notes);
      const excluded =
        stop?.excludedEmpnos ?? deriveExcludedTechs(combined, roster);
      let requested = stop?.requestedEmpno ?? deriveRequestedTech(combined, roster);
      if (requested && excluded.includes(requested)) requested = null;
      const attrLock = stop?.attrLock ?? deriveAttrLock(combined);

      // Eligible = in today, not out, not this tech, not excluded, fully skilled.
      const eligible = recoTechs.filter(
        (t) =>
          t.empno !== outEmpno &&
          !outSet.has(t.empno) &&
          !excluded.includes(t.empno) &&
          required.every((id) => t.skills.has(id)),
      );

      // Rank by marginal miles (geographic best-insertion picks the target;
      // the displayed number + Review preview are window-aware).
      const ranked = stop
        ? [...eligible].sort(
            (a, b) =>
              insertionCostMiles(a.stops, stop) -
              insertionCostMiles(b.stops, stop),
          )
        : eligible;

      const requestedAvailable = requested
        ? eligible.find((t) => t.empno === requested) ?? null
        : null;
      const suggested = requestedAvailable ?? ranked[0] ?? null;

      const warnings: string[] = [];
      if (requested && !requestedAvailable)
        warnings.push(
          outSet.has(requested)
            ? `Asked for ${firstNameFor(requested)} — he's also out today`
            : `Asked for ${firstNameFor(requested)} — not available for this call`,
        );
      if (attrLock)
        warnings.push("Note mentions a specific kind of tech — check before moving");
      if (!hasCoords) warnings.push("No map location — pick a tech yourself");
      if (required.length > 0 && eligible.length === 0)
        warnings.push(
          `No qualified tech available today (needs ${required
            .map(skillName)
            .join(", ")})`,
        );

      return {
        row: r,
        stop,
        required,
        excluded,
        requested,
        requestedAvailable: Boolean(requestedAvailable),
        attrLock,
        eligible: ranked,
        suggested,
        warnings,
        // pre-check only the clean, confident rows
        defaultInclude: Boolean(suggested && !attrLock && hasCoords),
      };
    });
  }, [orphans, recoTechs, techByEmpno, outEmpno, outSet, locationNotes, techs]); // eslint-disable-line react-hooks/exhaustive-deps

  const [plans, setPlans] = useState<Record<string, RowPlan>>({});
  const patchPlan = (d: string, p: Partial<RowPlan>) =>
    setPlans((cur) => ({ ...cur, [d]: { ...cur[d], ...p } }));

  const effective = analyses.map((a) => {
    const plan = plans[a.row.dispatch] ?? {};
    const target =
      plan.target !== undefined ? plan.target : a.suggested?.empno ?? null;
    const include = plan.moved
      ? false
      : plan.include !== undefined
        ? plan.include && Boolean(target)
        : a.defaultInclude && Boolean(target);
    return { ...a, target, include, moved: Boolean(plan.moved) };
  });
  const toMove = effective.filter((e) => e.include && e.target);

  // Window-aware added-miles for a row's chosen target (matches the Review
  // preview's model, so the number here never disagrees with the compare).
  const addedMiles = (targetEmpno: string | null, stop: RecoStop | null) => {
    if (!targetEmpno || !stop) return null;
    const t = techByEmpno.get(targetEmpno);
    if (!t) return null;
    return (
      routeMiles(optimizeBanded([...t.stops, stop])) -
      routeMiles(optimizeBanded(t.stops))
    );
  };

  const [applying, setApplying] = useState(false);
  const [appliedMoves, setAppliedMoves] = useState<
    { dispatch: string; label: string; toName: string }[] | null
  >(null);
  const [undoing, setUndoing] = useState(false);

  const applyAll = async () => {
    setApplying(true);
    const done: { dispatch: string; label: string; toName: string }[] = [];
    try {
      for (const e of toMove) {
        await setAssignment.mutateAsync({
          dispatch: e.row.dispatch,
          tech: e.target as string,
        });
        done.push({
          dispatch: e.row.dispatch,
          label: displayCustomerName(e.row.locname, e.row.customer_fullname),
          toName: nameFor(e.target as string),
        });
      }
      setAppliedMoves(done);
    } finally {
      setApplying(false);
    }
  };

  const undoAll = async () => {
    if (!appliedMoves) return;
    setUndoing(true);
    try {
      for (const m of appliedMoves) {
        await setAssignment.mutateAsync({ dispatch: m.dispatch, tech: outEmpno });
      }
      setAppliedMoves(null);
    } finally {
      setUndoing(false);
    }
  };

  const outName = nameFor(outEmpno);

  return (
    <Modal
      open
      onClose={guardedClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          Rebuild {outName}&apos;s day
          <InfoTip label="How the suggestions work">
            Each suggestion is measured against that tech&apos;s day as it stands — Review shows the full before / after
            compare. You can change any tech, or uncheck a call to leave it for later.
          </InfoTip>
        </span>
      }
      subtitle={
        orphans.length === 0
          ? "No open calls to move."
          : `${orphans.length} call${orphans.length === 1 ? "" : "s"} need${
              orphans.length === 1 ? "s" : ""
            } a new home — Pegasus suggests the closest qualified tech for each.`
      }
      icon={<UserX className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />}
      footer={
        <div className="flex items-center justify-end gap-2">
          {appliedMoves ? (
            <>
              <button
                type="button"
                onClick={undoAll}
                disabled={undoing}
                className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                <Undo2 className="h-3.5 w-3.5" />
                {undoing ? "Undoing…" : "Undo all"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyAll}
                disabled={toMove.length === 0 || applying}
                className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
              >
                {applying
                  ? "Moving…"
                  : `Move ${toMove.length} call${toMove.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </div>
      }
    >
        <div>
          {appliedMoves ? (
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                Moved {appliedMoves.length} call
                {appliedMoves.length === 1 ? "" : "s"}
              </div>
              <div className="space-y-1">
                {appliedMoves.map((m) => (
                  <div
                    key={m.dispatch}
                    className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs text-slate-700"
                  >
                    <span className="font-medium">{m.label}</span>
                    <ArrowRight className="h-3 w-3 text-slate-400" />
                    <span className="font-medium">{m.toName}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-slate-400">
                The board is updated. Undo puts every call back on {outName}.
              </p>
            </div>
          ) : skillsData == null ? (
            <p className="py-8 text-center text-xs text-slate-500">
              Checking skills and routes…
            </p>
          ) : orphans.length === 0 ? (
            <p className="py-8 text-center text-xs text-slate-500">
              {outName} has no open calls on this date — nothing to rebuild.
            </p>
          ) : (
            <div className="space-y-2">
              {effective.map((e) => {
                const r = e.row;
                const rw = resolveWindowAndPriority(r.window_code, r.notes);
                const wc = rw.timeWindow
                  ? windowColorClasses(rw.timeWindow)
                  : null;
                const mi = addedMiles(e.target, e.stop);
                return (
                  <div
                    key={r.dispatch}
                    className={cn(
                      "rounded-lg border px-3 py-2",
                      e.moved
                        ? "border-emerald-200 bg-emerald-50/60"
                        : e.include
                          ? "border-hairline bg-white"
                          : "border-hairline bg-slate-50/60",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={e.include}
                        disabled={e.moved || !e.target}
                        onChange={(ev) =>
                          patchPlan(r.dispatch, { include: ev.target.checked })
                        }
                        className="h-3.5 w-3.5"
                        aria-label="Include in the move"
                      />
                      <span className="w-14 shrink-0 text-xs tabular-nums text-slate-500">
                        {formatTime(r.tpromtime) || "—"}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-800">
                        {displayCustomerName(r.locname, r.customer_fullname)}
                        {r.city ? (
                          <span className="font-normal text-slate-400">
                            {" "}
                            ({r.city})
                          </span>
                        ) : null}
                      </span>
                      {rw.timeWindow && wc && (
                        <span
                          className={cn(
                            "shrink-0 rounded px-1 text-[11px] font-semibold",
                            wc.bg,
                            wc.text,
                          )}
                        >
                          {rw.timeWindow}
                        </span>
                      )}
                    </div>

                    <div className="mt-1.5 flex items-center gap-2 pl-6">
                      {e.moved ? (
                        <span className="text-xs font-medium text-emerald-700">
                          Moved ✓
                        </span>
                      ) : (
                        <>
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                          <select
                            value={e.target ?? ""}
                            onChange={(ev) =>
                              patchPlan(r.dispatch, {
                                target: ev.target.value || null,
                              })
                            }
                            className="max-w-[180px] rounded-md border border-hairline bg-white px-1.5 py-1 text-xs"
                          >
                            <option value="">— pick a tech —</option>
                            {e.eligible.map((t) => (
                              <option key={t.empno} value={t.empno}>
                                {t.name}
                                {t.stops.length === 0 ? " (no calls yet)" : ""}
                              </option>
                            ))}
                          </select>
                          {e.target &&
                            e.requested === e.target &&
                            e.requestedAvailable && (
                              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                                asked for
                              </span>
                            )}
                          {mi != null && (
                            <span className="text-[11px] tabular-nums text-slate-500">
                              {(techByEmpno.get(e.target as string)?.stops
                                .length ?? 0) === 0
                                ? "first call of his day"
                                : mi > 0.05
                                  ? `+${mi.toFixed(1)} mi · ~${Math.round(driveMinutes(mi))} min added`
                                  : "on his way"}
                            </span>
                          )}
                          {e.target && e.stop && (
                            <button
                              type="button"
                              onClick={() =>
                                setPendingMove({
                                  dispatch: r.dispatch,
                                  label: displayCustomerName(
                                    r.locname,
                                    r.customer_fullname,
                                  ),
                                  fromEmpno: outEmpno,
                                  fromName: outName,
                                  toEmpno: e.target as string,
                                  toName: nameFor(e.target as string),
                                  savingMiles: 0,
                                })
                              }
                              className="ml-auto shrink-0 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
                            >
                              Review
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {e.warnings.length > 0 && !e.moved && (
                      <div className="mt-1 space-y-0.5 pl-6">
                        {e.warnings.map((w, i) => (
                          <div key={i} className="text-[11px] text-amber-700">
                            ⚠ {w}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {/* Per-row Review — the same schedule compare as a normal move. */}
      {pendingMove && (
        <div onClick={(e) => e.stopPropagation()}>
          <MovePreview
            move={pendingMove}
            rows={baseRows}
            recoTechs={recoTechs}
            pending={setAssignment.isPending}
            onClose={() => setPendingMove(null)}
            onConfirm={(targetOrder, editedOrder) => {
              setAssignment.mutate(
                { dispatch: pendingMove.dispatch, tech: pendingMove.toEmpno },
                {
                  onSuccess: () => {
                    if (editedOrder) {
                      savePlan.mutate({
                        date,
                        tech: pendingMove.toEmpno,
                        sequence: targetOrder.map((d) => ({ d, pin: false })),
                      });
                    }
                    patchPlan(pendingMove.dispatch, { moved: true });
                    setPendingMove(null);
                  },
                },
              );
            }}
          />
        </div>
      )}
    </Modal>
  );
}
